/** Prompts for who's requesting a rule set be locked into .idsx, and any
 * project/context worth attaching -- see app/ids/lock.py's provenance
 * fields. Everything here is optional except Name; email defaults to the
 * signed-in account's own, editable in case the lock is being made on
 * someone else's behalf. */

import { useEffect, useState } from 'react';
import * as api from '../lib/api';
import { ApiError } from '../lib/api';
import type { LockRequestInput } from '../lib/api';
import { Callout } from './primitives';

interface LockRequestDialogProps {
  documentName: string;
  onConfirm: (input: LockRequestInput) => Promise<void>;
  onCancel: () => void;
}

export function LockRequestDialog({ documentName, onConfirm, onCancel }: LockRequestDialogProps) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [projectName, setProjectName] = useState('');
  const [comments, setComments] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .getMe()
      .then((me) => {
        if (!cancelled && me.account_email) setEmail(me.account_email);
      })
      .catch(() => {
        // Prefill is a convenience, not a requirement -- an anonymous/free
        // caller (or a network hiccup) just leaves the field blank to fill
        // in by hand.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      await onConfirm({
        requestedByName: name.trim(),
        requestedByEmail: email.trim(),
        projectName: projectName.trim(),
        comments: comments.trim(),
      });
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not lock that specification.');
      setBusy(false);
    }
  }

  return (
    <div className="overlay" role="dialog" aria-modal="true" aria-label="Lock rule set">
      <div className="card">
        <div className="card-head">
          <h2>Lock “{documentName}”</h2>
        </div>
        <div className="card-body stack">
          <p className="hint">
            Signed into the .idsx itself — shown on the resulting validation report and to
            anyone who decrypts this file for audit. Everything below is optional.
          </p>
          <div className="form-grid cols-2">
            <label className="field">
              <span>Name</span>
              <input
                type="text"
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="e.g. Jane Smith"
                autoFocus
              />
            </label>
            <label className="field">
              <span>Email</span>
              <input
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="jane@tfnsw.gov.au"
              />
            </label>
          </div>
          <label className="field">
            <span>Project name</span>
            <input
              type="text"
              value={projectName}
              onChange={(event) => setProjectName(event.target.value)}
              placeholder="e.g. Bridge R26 resubmission"
            />
          </label>
          <label className="field">
            <span>Comments</span>
            <input
              type="text"
              value={comments}
              onChange={(event) => setComments(event.target.value)}
              placeholder="Any context worth attaching — e.g. why this lock was made, or what to check for"
            />
            <span className="hint">Shown alongside the lock on the report and in an audit.</span>
          </label>
          {error && <Callout variant="error">{error}</Callout>}
          <div className="row" style={{ justifyContent: 'flex-end' }}>
            <button type="button" className="btn" onClick={onCancel} disabled={busy}>
              Cancel
            </button>
            <button type="button" className="btn btn-accent" onClick={() => void submit()} disabled={busy}>
              {busy ? 'Locking…' : 'Lock (.idsx)'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
